import { useState, useEffect } from "react";

/**
 * Custom hook to detect media queries and respond to viewport changes
 * Useful for responsive design logic in React components
 *
 * @param query - Media query string (e.g., "(max-width: 768px)")
 * @returns boolean - Whether the media query matches
 *
 * @example
 * ```tsx
 * const isMobile = useMediaQuery("(max-width: 768px)");
 * const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1024px)");
 * const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
 * ```
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // Check if we're in the browser environment
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    // Ensure we're in the browser environment
    if (typeof window === "undefined") return;

    const mediaQueryList = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQueryList.matches);

    // Handler for media query changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add listener
    mediaQueryList.addEventListener("change", handleChange);

    // Cleanup listener on unmount
    return () => {
      mediaQueryList.removeEventListener("change", handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Custom hook with predefined breakpoints for common responsive scenarios
 * Provides convenient boolean flags for different screen sizes
 *
 * @returns object with boolean flags for different breakpoints
 *
 * @example
 * ```tsx
 * const { isMobile, isTablet, isDesktop, isLargeDesktop } = useResponsiveBreakpoints();
 *
 * if (isMobile) {
 *   return <MobileComponent />;
 * }
 * ```
 */
export function useResponsiveBreakpoints() {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1024px)");
  const isDesktop = useMediaQuery(
    "(min-width: 1025px) and (max-width: 1440px)"
  );
  const isLargeDesktop = useMediaQuery("(min-width: 1441px)");

  // Convenience flags
  const isTabletOrMobile = useMediaQuery("(max-width: 1024px)");
  const isDesktopOrLarger = useMediaQuery("(min-width: 1025px)");

  return {
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isTabletOrMobile,
    isDesktopOrLarger,
  };
}

/**
 * Custom hook that returns the current viewport width and height
 * Updates on window resize events
 *
 * @returns object with width and height in pixels
 *
 * @example
 * ```tsx
 * const { width, height } = useViewportSize();
 * const itemsPerRow = width > 768 ? 2 : 1;
 * ```
 */
export function useViewportSize() {
  const [viewport, setViewport] = useState<{ width: number; height: number }>(
    () => {
      if (typeof window !== "undefined") {
        return {
          width: window.innerWidth,
          height: window.innerHeight,
        };
      }
      return { width: 0, height: 0 };
    }
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial value
    handleResize();

    // Add listener
    window.addEventListener("resize", handleResize, { passive: true });

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return viewport;
}
